import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  ActionIcon,
  Alert,
  Button,
  Checkbox,
  Flex,
  Group,
  List,
  Notification,
  Paper,
  Select,
  Stack,
  Tabs,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import * as z from 'zod';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import {
  IconArrowLeft,
  IconDownload,
  IconInfoCircle,
  IconTrash,
  IconUpload,
} from '@tabler/icons-react';
import { theme } from '../../../global/style/theme.tsx';
import { Loading } from '../../../global/component/organism/Loading/Loading.tsx';
import {
  useScenario,
  useCreateScenario,
  useUpdateScenario,
  useAddScenarioQuestionnaire,
  useRemoveScenarioQuestionnaire,
  useAddScenarioJournal,
  useRemoveScenarioJournal,
  useAddScenarioClinicalNoteType,
  useRemoveScenarioClinicalNoteType,
  useImportScenarioQuestionnaire,
  useImportScenarioJournal,
  useImportScenarioPhysiological,
  useImportScenarioDocument,
  useImportScenarioPrescription,
  useImportScenarioLifeEvent,
  useImportScenarioClinicalNote,
} from '../../../global/entities/Scenario/queries/useScenarios.ts';
import {
  ClinicalNoteType,
  IScenarioWrite,
} from '../../../global/entities/Scenario/Scenario.ts';
import { useGetQuestionnaires } from '../../../global/entities/Questionnaires/queries/useGetQuestionnaires.tsx';
import { useJournals } from '../../../global/entities/Journal/queries/useJournals.ts';
import ScenarioSlotsEditor from './ScenarioSlotsEditor.tsx';
import ApplyScenarioModal from '../components/ApplyScenarioModal.tsx';
import {
  IScenarioImportUnresolved,
  ScenariosApi,
} from '../../../global/apis/ScenariosApi/ScenariosApi.ts';
import { downloadBlob } from '../../../global/helpers/downloadBlob.ts';
import { AppMedError } from '../../../global/error/AppMedError.ts';

const initValues: IScenarioWrite = {
  nameEn: '',
  nameFr: '',
  description: '',
  isActive: 1,
};

const ScenarioForm: React.FC = () => {
  const { t, i18n } = useTranslation();
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isCreate = params.id === '0';
  const scenarioId = Number(params.id);

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState<string | null>('general');
  const [applyOpen, setApplyOpen] = useState(false);
  const [importMessage, setImportMessage] = useState<string | null>(null);
  const [importErrors, setImportErrors] = useState<
    IScenarioImportUnresolved[] | null
  >(null);
  const [importFailure, setImportFailure] = useState<string | null>(null);
  const questionnaireFileRef = useRef<HTMLInputElement>(null);
  const journalFileRef = useRef<HTMLInputElement>(null);
  const physiologicalFileRef = useRef<HTMLInputElement>(null);
  const lifeEventFileRef = useRef<HTMLInputElement>(null);
  const documentFileRef = useRef<HTMLInputElement>(null);
  const clinicalNoteFileRef = useRef<HTMLInputElement>(null);
  const prescriptionFileRef = useRef<HTMLInputElement>(null);
  const importQuestionnaire = useImportScenarioQuestionnaire(scenarioId);
  const importJournal = useImportScenarioJournal(scenarioId);
  const importPhysiological = useImportScenarioPhysiological(scenarioId);
  const importLifeEvent = useImportScenarioLifeEvent(scenarioId);
  const importDocument = useImportScenarioDocument(scenarioId);
  const importClinicalNote = useImportScenarioClinicalNote(scenarioId);
  const importPrescription = useImportScenarioPrescription(scenarioId);

  const { data: scenario, isLoading: scenarioLoading } = useScenario(
    isCreate ? 0 : scenarioId,
  );
  const { data: questionnaires } = useGetQuestionnaires();
  const { data: journals } = useJournals();

  const createMutation = useCreateScenario();
  const updateMutation = useUpdateScenario();
  const addQuestionnaire = useAddScenarioQuestionnaire(scenarioId);
  const removeQuestionnaire = useRemoveScenarioQuestionnaire(scenarioId);
  const addJournal = useAddScenarioJournal(scenarioId);
  const removeJournal = useRemoveScenarioJournal(scenarioId);
  const addClinicalNoteType = useAddScenarioClinicalNoteType(scenarioId);
  const removeClinicalNoteType = useRemoveScenarioClinicalNoteType(scenarioId);

  const schema = useMemo(
    () =>
      z.object({
        nameEn: z
          .string()
          .min(1, { message: t('formError.requiredName') + ' (EN)' }),
        nameFr: z
          .string()
          .min(1, { message: t('formError.requiredName') + ' (FR)' }),
        description: z.string().optional().default(''),
        isActive: z.number(),
      }),
    [t],
  );

  const form = useForm<IScenarioWrite>({
    mode: 'controlled',
    initialValues: initValues,
    validate: zodResolver(schema),
  });

  useEffect(() => {
    if (scenario && !isCreate) {
      const values: IScenarioWrite = {
        nameEn: scenario.nameEn || '',
        nameFr: scenario.nameFr || '',
        description: scenario.description || '',
        isActive: scenario.isActive,
      };
      form.setInitialValues(values);
      form.setValues(values);
      form.resetDirty(values);
    }
  }, [scenario, isCreate]);

  const handleCancel = () => navigate('/adminDashboard/scenarios');

  const handleSubmit = async (values: IScenarioWrite) => {
    setError(null);
    setIsSubmitting(true);
    try {
      if (isCreate) {
        const newId = await createMutation.mutateAsync(values);
        navigate(`/adminDashboard/scenarios/${newId}`);
      } else
        await updateMutation.mutateAsync({ id: scenarioId, model: values });
    } catch (e: any) {
      setError(e?.message ?? t('common.unexpectedError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const lang = i18n.language?.startsWith('fr') ? 'fr' : 'en';

  if (!isCreate && scenarioLoading)
    return <Loading visible={true} loadingMessage={t('common.loading')} />;

  // Available pickers (filtered to exclude already-selected items)
  const selectedQuestionnaireIds = new Set(
    scenario?.questionnaires.map((q) => q.questionnaireID) ?? [],
  );
  const selectedJournalIds = new Set(
    scenario?.journals.map((j) => j.journalID) ?? [],
  );

  const availableQuestionnaireOptions = (questionnaires ?? [])
    .filter((q: any) => !selectedQuestionnaireIds.has(q.id))
    .map((q: any) => ({
      value: q.id.toString(),
      label: q.name ?? q.shortName ?? `#${q.id}`,
    }));

  const availableJournalOptions = (journals ?? [])
    .filter((j: any) => !selectedJournalIds.has(j.id))
    .map((j: any) => ({ value: j.id.toString(), label: j.name ?? `#${j.id}` }));

  const noteTypeOptions = [
    {
      value: ClinicalNoteType.PhysicalExamination.toString(),
      label: t('scenario.noteType.physicalExamination'),
    },
    {
      value: ClinicalNoteType.MedicalHistory.toString(),
      label: t('scenario.noteType.medicalHistory'),
    },
    {
      value: ClinicalNoteType.Counselling.toString(),
      label: t('scenario.noteType.counselling'),
    },
  ];

  const downloadFile = async (
    fetchFn: () => Promise<{ blob: Blob; filename: string }>,
  ) => {
    setImportMessage(null);
    setImportErrors(null);
    try {
      const { blob, filename } = await fetchFn();
      downloadBlob(blob, filename);
    } catch (e: any) {
      setError(e?.message ?? t('common.unexpectedError'));
    }
  };

  const handleImport = async (
    file: File,
    importer: typeof importQuestionnaire,
  ) => {
    setImportMessage(null);
    setImportErrors(null);
    setImportFailure(null);
    try {
      const result = await importer.mutateAsync(file);
      if (result.unresolved && result.unresolved.length > 0)
        setImportErrors(result.unresolved);
       else {
        setImportMessage(
          t('scenario.importSuccess', { count: result.insertedRowCount }),
        );
      }
    } catch (e: unknown) {
      setImportFailure(importFailureDetail(e));
    }
  };

  const importFailureDetail = (e: unknown): string => {
    if (!(e instanceof AppMedError)) return '';
    if (e.httpStatusCode === 413) return t('scenario.importTooLarge');
    return e.message;
  };

  const hasSlots = (scenario?.slots.length ?? 0) > 0;
  const hasExcelData = (scenario?.slots ?? []).some(
    (s) =>
      s.questionnaireEntries.length > 0 ||
      s.journalEntries.length > 0 ||
      s.physiologicalEntries.length > 0 ||
      (s.lifeEventEntries ?? []).length > 0 ||
      (s.documentEntries ?? []).length > 0 ||
      s.clinicalNoteEntries.length > 0 ||
      (s.prescriptionEntries ?? []).length > 0,
  );

  return (
    <>
      <Flex mb="lg" mt="lg" align="center" justify="space-between">
        <Button
          leftSection={<IconArrowLeft color={theme.colors?.blue?.toString()} />}
          onClick={handleCancel}
          variant="transparent"
        >
          <Title c="black" order={4}>
            {isCreate ? t('scenario.addNew') : t('scenario.edit')}
          </Title>
        </Button>
        {!isCreate && scenario && (
          <Group>
            <Button
              color="red"
              disabled={form.isDirty() || scenario.slots.length === 0}
              onClick={() => setApplyOpen(true)}
            >
              {t('scenario.apply')}
            </Button>
          </Group>
        )}
      </Flex>
      {!isCreate && scenario && (
        <ApplyScenarioModal
          scenario={scenario}
          opened={applyOpen}
          onClose={() => setApplyOpen(false)}
        />
      )}

      {!isCreate && scenario && (
        <Paper p="md" mb="md" withBorder>
          <Stack gap="sm">
            <Title order={5}>{t('scenario.excelTools')}</Title>
            {!hasSlots && (
              <Alert color="yellow" icon={<IconInfoCircle size={16} />}>
                {t('scenario.importNeedsSlots')}
              </Alert>
            )}
            <Group>
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                disabled={!hasSlots}
                onClick={() =>
                  downloadFile(() =>
                    ScenariosApi.downloadQuestionnaireTemplate(scenarioId),
                  )
                }
              >
                {t('scenario.downloadQuestionnaireTemplate')}
              </Button>
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                disabled={!hasSlots}
                onClick={() =>
                  downloadFile(() =>
                    ScenariosApi.downloadQuestionnaireData(scenarioId),
                  )
                }
              >
                {t('scenario.downloadQuestionnaireData')}
              </Button>
              <Button
                leftSection={<IconUpload size={16} />}
                loading={importQuestionnaire.isPending}
                disabled={!hasSlots}
                onClick={() => questionnaireFileRef.current?.click()}
              >
                {t('scenario.importQuestionnaire')}
              </Button>
              <input
                ref={questionnaireFileRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImport(f, importQuestionnaire);
                  e.target.value = '';
                }}
              />
            </Group>
            <Group>
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                disabled={!hasSlots}
                onClick={() =>
                  downloadFile(() =>
                    ScenariosApi.downloadJournalTemplate(scenarioId),
                  )
                }
              >
                {t('scenario.downloadJournalTemplate')}
              </Button>
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                disabled={!hasSlots}
                onClick={() =>
                  downloadFile(() =>
                    ScenariosApi.downloadJournalData(scenarioId),
                  )
                }
              >
                {t('scenario.downloadJournalData')}
              </Button>
              <Button
                leftSection={<IconUpload size={16} />}
                loading={importJournal.isPending}
                disabled={!hasSlots}
                onClick={() => journalFileRef.current?.click()}
              >
                {t('scenario.importJournal')}
              </Button>
              <input
                ref={journalFileRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImport(f, importJournal);
                  e.target.value = '';
                }}
              />
            </Group>
            <Group>
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                disabled={!hasSlots}
                onClick={() =>
                  downloadFile(() =>
                    ScenariosApi.downloadPhysiologicalTemplate(scenarioId),
                  )
                }
              >
                {t('scenario.downloadPhysiologicalTemplate')}
              </Button>
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                disabled={!hasSlots}
                onClick={() =>
                  downloadFile(() =>
                    ScenariosApi.downloadPhysiologicalData(scenarioId),
                  )
                }
              >
                {t('scenario.downloadPhysiologicalData')}
              </Button>
              <Button
                leftSection={<IconUpload size={16} />}
                loading={importPhysiological.isPending}
                disabled={!hasSlots}
                onClick={() => physiologicalFileRef.current?.click()}
              >
                {t('scenario.importPhysiological')}
              </Button>
              <input
                ref={physiologicalFileRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImport(f, importPhysiological);
                  e.target.value = '';
                }}
              />
            </Group>
            <Group>
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                disabled={!hasSlots}
                onClick={() =>
                  downloadFile(() =>
                    ScenariosApi.downloadLifeEventTemplate(scenarioId),
                  )
                }
              >
                {t('scenario.downloadLifeEventTemplate')}
              </Button>
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                disabled={!hasSlots}
                onClick={() =>
                  downloadFile(() =>
                    ScenariosApi.downloadLifeEventData(scenarioId),
                  )
                }
              >
                {t('scenario.downloadLifeEventData')}
              </Button>
              <Button
                leftSection={<IconUpload size={16} />}
                loading={importLifeEvent.isPending}
                disabled={!hasSlots}
                onClick={() => lifeEventFileRef.current?.click()}
              >
                {t('scenario.importLifeEvent')}
              </Button>
              <input
                ref={lifeEventFileRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImport(f, importLifeEvent);
                  e.target.value = '';
                }}
              />
            </Group>
            <Group>
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                disabled={!hasSlots}
                onClick={() =>
                  downloadFile(() =>
                    ScenariosApi.downloadDocumentTemplate(scenarioId),
                  )
                }
              >
                {t('scenario.downloadDocumentTemplate')}
              </Button>
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                disabled={!hasSlots}
                onClick={() =>
                  downloadFile(() =>
                    ScenariosApi.downloadDocumentData(scenarioId),
                  )
                }
              >
                {t('scenario.downloadDocumentData')}
              </Button>
              <Button
                leftSection={<IconUpload size={16} />}
                loading={importDocument.isPending}
                disabled={!hasSlots}
                onClick={() => documentFileRef.current?.click()}
              >
                {t('scenario.importDocument')}
              </Button>
              <input
                ref={documentFileRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImport(f, importDocument);
                  e.target.value = '';
                }}
              />
            </Group>
            <Text size="xs" c="dimmed">
              {t('scenario.documentStubHint')}
            </Text>
            <Group>
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                disabled={!hasSlots}
                onClick={() =>
                  downloadFile(() =>
                    ScenariosApi.downloadClinicalNoteTemplate(scenarioId),
                  )
                }
              >
                {t('scenario.downloadClinicalNoteTemplate')}
              </Button>
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                disabled={!hasSlots}
                onClick={() =>
                  downloadFile(() =>
                    ScenariosApi.downloadClinicalNoteData(scenarioId),
                  )
                }
              >
                {t('scenario.downloadClinicalNoteData')}
              </Button>
              <Button
                leftSection={<IconUpload size={16} />}
                loading={importClinicalNote.isPending}
                disabled={!hasSlots}
                onClick={() => clinicalNoteFileRef.current?.click()}
              >
                {t('scenario.importClinicalNote')}
              </Button>
              <input
                ref={clinicalNoteFileRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImport(f, importClinicalNote);
                  e.target.value = '';
                }}
              />
            </Group>
            <Text size="xs" c="dimmed">
              {t('scenario.clinicalNoteImportHint')}
            </Text>
            <Group>
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                disabled={!hasSlots}
                onClick={() =>
                  downloadFile(() =>
                    ScenariosApi.downloadPrescriptionTemplate(scenarioId),
                  )
                }
              >
                {t('scenario.downloadPrescriptionTemplate')}
              </Button>
              <Button
                variant="outline"
                leftSection={<IconDownload size={16} />}
                disabled={!hasSlots}
                onClick={() =>
                  downloadFile(() =>
                    ScenariosApi.downloadPrescriptionData(scenarioId),
                  )
                }
              >
                {t('scenario.downloadPrescriptionData')}
              </Button>
              <Button
                leftSection={<IconUpload size={16} />}
                loading={importPrescription.isPending}
                disabled={!hasSlots}
                onClick={() => prescriptionFileRef.current?.click()}
              >
                {t('scenario.importPrescription')}
              </Button>
              <input
                ref={prescriptionFileRef}
                type="file"
                accept=".xlsx"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleImport(f, importPrescription);
                  e.target.value = '';
                }}
              />
            </Group>
            <Text size="xs" c="dimmed">
              {t('scenario.prescriptionImportHint')}
            </Text>
            {importMessage && (
              <Notification
                color="green"
                onClose={() => setImportMessage(null)}
                title={importMessage}
              />
            )}
            {importErrors && (
              <Alert
                color="red"
                title={t('scenario.importUnresolvedTitle')}
                onClose={() => setImportErrors(null)}
                withCloseButton
              >
                <Text size="sm" mb="xs">
                  {t('scenario.importUnresolvedHint')}
                </Text>
                <List size="sm">
                  {importErrors.map((u, i) => (
                    <List.Item key={i}>
                      {u.sheet} · row {u.row} · {u.field}: <b>{u.value}</b>
                    </List.Item>
                  ))}
                </List>
              </Alert>
            )}
            {importFailure !== null && (
              <Alert
                color="red"
                title={t('scenario.importFailed')}
                onClose={() => setImportFailure(null)}
                withCloseButton
                closeButtonLabel={t('button.close')}
              >
                {importFailure}
              </Alert>
            )}
          </Stack>
        </Paper>
      )}

      <Paper p="md" style={{ minWidth: '100%', width: 'max-content' }}>
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="general">{t('scenario.tab.general')}</Tabs.Tab>
              <Tabs.Tab value="content" disabled={isCreate}>
                {t('scenario.tab.content')}
              </Tabs.Tab>
              <Tabs.Tab value="slots" disabled={isCreate}>
                {t('scenario.tab.slots')}
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="general" pt="md">
              <Stack gap="md">
                <TextInput
                  label={t('formGlobal.name') + ' (EN)'}
                  {...form.getInputProps('nameEn')}
                />
                <TextInput
                  label={t('formGlobal.name') + ' (FR)'}
                  {...form.getInputProps('nameFr')}
                />
                <Textarea
                  label={t('formGlobal.description')}
                  autosize
                  minRows={2}
                  {...form.getInputProps('description')}
                />
                <Checkbox
                  label={t('formGlobal.active')}
                  checked={form.values.isActive === 1}
                  onChange={(e) =>
                    form.setFieldValue(
                      'isActive',
                      e.currentTarget.checked ? 1 : 0,
                    )
                  }
                />
                {error && (
                  <Notification color="red" onClose={() => setError(null)}>
                    {error}
                  </Notification>
                )}
                <Group justify="flex-end" gap="sm">
                  <Button variant="outline" onClick={handleCancel}>
                    {t('button.cancel')}
                  </Button>
                  <Button type="submit" loading={isSubmitting}>
                    {t('button.save')}
                  </Button>
                </Group>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="content" pt="md">
              <Stack gap="lg">
                <Stack gap="xs">
                  <Title order={5}>{t('scenario.questionnaires')}</Title>
                  <Group>
                    <Select
                      placeholder={t('scenario.addQuestionnaire')}
                      data={availableQuestionnaireOptions}
                      searchable
                      onChange={async (val) => {
                        if (!val) return;
                        await addQuestionnaire.mutateAsync({
                          questionnaireId: parseInt(val),
                          orderIndex: scenario?.questionnaires.length ?? 0,
                        });
                      }}
                      value={null}
                      style={{ minWidth: 320 }}
                    />
                  </Group>
                  <Table verticalSpacing="xs">
                    <Table.Tbody>
                      {scenario?.questionnaires.map((q) => (
                        <Table.Tr key={q.id}>
                          <Table.Td>
                            <Text fw={500}>
                              {lang === 'fr' ? q.shortNameFr : q.shortNameEn}
                            </Text>
                          </Table.Td>
                          <Table.Td style={{ width: 50 }}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() =>
                                removeQuestionnaire.mutate(q.questionnaireID)
                              }
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Stack>

                <Stack gap="xs">
                  <Title order={5}>{t('scenario.journals')}</Title>
                  <Group>
                    <Select
                      placeholder={t('scenario.addJournal')}
                      data={availableJournalOptions}
                      searchable
                      onChange={async (val) => {
                        if (!val) return;
                        await addJournal.mutateAsync({
                          journalId: parseInt(val),
                          orderIndex: scenario?.journals.length ?? 0,
                        });
                      }}
                      value={null}
                      style={{ minWidth: 320 }}
                    />
                  </Group>
                  <Table verticalSpacing="xs">
                    <Table.Tbody>
                      {scenario?.journals.map((j) => (
                        <Table.Tr key={j.id}>
                          <Table.Td>
                            <Text fw={500}>
                              {lang === 'fr' ? j.nameFr : j.nameEn}
                            </Text>
                          </Table.Td>
                          <Table.Td style={{ width: 50 }}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() => removeJournal.mutate(j.journalID)}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Stack>

                <Stack gap="xs">
                  <Title order={5}>{t('scenario.clinicalNoteTypes')}</Title>
                  <Group>
                    <Select
                      placeholder={t('scenario.addClinicalNoteType')}
                      data={noteTypeOptions}
                      onChange={async (val) => {
                        if (!val) return;
                        await addClinicalNoteType.mutateAsync({
                          noteTypeID: parseInt(val),
                          questionnaireID: null,
                          orderIndex: scenario?.clinicalNoteTypes.length ?? 0,
                        });
                      }}
                      value={null}
                      style={{ minWidth: 320 }}
                    />
                  </Group>
                  <Table verticalSpacing="xs">
                    <Table.Tbody>
                      {scenario?.clinicalNoteTypes.map((c) => (
                        <Table.Tr key={c.id}>
                          <Table.Td>
                            <Text fw={500}>
                              {noteTypeOptions.find(
                                (o) => o.value === c.noteTypeID.toString(),
                              )?.label ?? c.noteTypeID}
                              {c.questionnaireShortNameEn
                                ? ` — ${c.questionnaireShortNameEn}`
                                : ''}
                            </Text>
                          </Table.Td>
                          <Table.Td style={{ width: 50 }}>
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={() =>
                                removeClinicalNoteType.mutate(c.id)
                              }
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Stack>
              </Stack>
            </Tabs.Panel>

            <Tabs.Panel value="slots" pt="md">
              {scenario && (
                <Stack gap="md">
                  {hasExcelData && (
                    <Alert
                      color="blue"
                      icon={<IconInfoCircle size={16} />}
                      title={t('scenario.entriesReadOnly')}
                    />
                  )}
                  <ScenarioSlotsEditor
                    scenario={scenario}
                    readOnly={hasExcelData}
                  />
                </Stack>
              )}
            </Tabs.Panel>
          </Tabs>
        </form>
      </Paper>
    </>
  );
};

export default ScenarioForm;
